/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Divider from '@material-ui/core/Divider';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import { Field, FieldArray, Formik } from 'formik';
import { Grid, Hidden } from '@material-ui/core';
import { last } from 'lodash';
import first from 'lodash/first';
import { dateFormats, TP } from '../../../constants/consts';
import { EditModeCardSection } from '../../../components/EditModeCard';
import Button from '../../../components/Button';
import { borderRadius, colors, fontSize } from '../../../constants/styleConstants';
import Title from '../../../components/Title';
import SubmitModalLayout from '../../../components/SubmitModalLayout';
import FormikAutocompleteSelect from '../../../components/Fields/FormikAutocompleteSelect';
import AddAnotherField from '../../../components/AddAnotherField';
import { createDate } from '../../../utils/date';

const useStyles = makeStyles(theme => ({
  boldText: {
    fontWeight: 'bold!important',
    color: colors.secondaryTitleColor,
  },
  footerBtn: {
    marginRight: '28px',
  },
  editCard: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  divider: {
    margin: '0 5px',
    backgroundColor: colors.secondaryTitleColor,
  },
  subTitleItem: {
    lineHeight: '17px',
  },
  title2: {
    display: 'flex',
  },
  fieldLabel: {
    backgroundColor: 'white',
    color: `${colors.cancelButton}`,
    fontWeight: `bold`,
    padding: '8px 0px 8px 10px',
    border: `1px solid ${colors.fieldsBorderGray}`,
    borderRadius: borderRadius.fields,
    fontSize: fontSize.medium,
    lineHeight: '28px',
    margin: '4px 0',
    maxWidth: '250px',
    minHeight: '46px',
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
  },
}));

const CastFieldArray = ({
  values,
  roles,
  castType,
  setFieldNameToFocus,
  fieldToFocus,
  getFilteredRoles,
  options,
  classes,
}) =>
  roles?.map(role => {
    const casts = get(values, `${castType}.${role?.id}`, [undefined]);
    const availableProfessions = get(options, role?.id, []);
    return (
      <Grid item xs={12} container key={role?.name}>
        <Grid item sm={4} xs={12}>
          <div className={classes.fieldLabel}>{role?.name || role?.original_name}</div>
        </Grid>
        <Grid item xs={12} sm={8} style={{ marginTop: 4 }}>
          {casts.map((castMember, castMemberIndex) => (
            <FieldArray name={`${castType}.${role?.id}`}>
              {arrayHelpers => (
                <AddAnotherField
                  key={`${castMember?.id}-${fieldToFocus}`}
                  values={casts}
                  removeFunc={() => {
                    arrayHelpers.remove(castMemberIndex);
                    setFieldNameToFocus(`${castType}.${role?.id}.${castMemberIndex === 0 ? 0 : castMemberIndex - 1}`);
                  }}
                  addFunc={() => {
                    arrayHelpers.push(undefined);
                    setFieldNameToFocus(`${castType}.${role?.id}.${castMemberIndex + 1}`);
                  }}
                  isLast={availableProfessions?.length > 1 && castMemberIndex === casts.length - 1}
                  addDisabled={availableProfessions.length === castMemberIndex + 1 || !last(casts)?.value}
                >
                  <Field
                    tabIndex={0}
                    autoFocus={fieldToFocus === `${castType}.${role?.id}.${castMemberIndex}`}
                    instanceId={castMember?.id}
                    name={`${castType}.${role?.id}.${castMemberIndex}`}
                    component={FormikAutocompleteSelect}
                    options={getFilteredRoles(options, role?.id, castType)}
                    blurOnSelect={false}
                    isClearable
                  />
                </AddAnotherField>
              )}
            </FieldArray>
          ))}
        </Grid>
      </Grid>
    );
  });

CastFieldArray.propTypes = {
  values: PropTypes.object.isRequired,
  roles: PropTypes.array.isRequired,
  castType: PropTypes.oneOf(['crew', 'cast']).isRequired,
  setFieldNameToFocus: PropTypes.func.isRequired,
  fieldToFocus: PropTypes.string,
  getFilteredRoles: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  classes: PropTypes.object,
};

const CastEditModal = ({
  isOpened,
  t,
  onClose,
  performance,
  production,
  submitFunc,
  professions,
  roles,
  rolesOptions,
  professionsOptions,
}) => {
  const { performanceToUpdate, times } = performance;
  const standardCast = first(performanceToUpdate?.time)?.cast || performanceToUpdate?.cast;
  const standardCrew = first(performanceToUpdate?.time)?.crew || performanceToUpdate?.crew;
  const castDate = performanceToUpdate?.date; // castDate: yyyy-mm-dd
  const getCastStructure = object => ({ cast: object?.cast || {}, crew: object?.crew || {} });
  const getInitialCast = () => {
    if (times?.length > 0) {
      return getCastStructure(times[0]);
    }
    return { cast: standardCast, crew: standardCrew };
  };
  const getInitialTime = () => {
    if (times?.length > 0) {
      return times[0];
    }
    return { time: null, date: castDate, cast: standardCast, crew: standardCrew };
  };

  const [timeValue, setTimeValue] = React.useState(getInitialTime());
  const [currentCast, setCurrentCast] = React.useState(getInitialCast());
  const [fieldToFocus, setFieldNameToFocus] = React.useState(null);

  React.useEffect(() => {
    if (times?.length > 0) {
      setTimeValue(times[0]);
      setCurrentCast(getCastStructure(times[0]));
    }
  }, [performance]);

  React.useEffect(() => {
    setCurrentCast(getCastStructure(timeValue));
  }, [timeValue]);

  const [applyForAllTimes, setApplyForAllTimes] = React.useState(false);
  const classes = useStyles();

  const castRef = React.useRef(null);
  const submitCastAndTime = forAllTimes => {
    setApplyForAllTimes(forAllTimes);
    get(castRef.current, 'submitForm', () => {})();
  };

  const getCast = values => {
    let updatedCastTimes = [...times];
    const cast = get(values, 'cast', {});
    const crew = get(values, 'crew', {});
    if (applyForAllTimes) {
      updatedCastTimes = times.map(timeInfo => ({
        ...timeInfo,
        cast,
        crew,
      }));
    } else {
      const updatedTime = { ...timeValue, cast, crew };
      const indexToReplace = findIndex(updatedCastTimes, { time: timeValue?.time });
      updatedCastTimes[indexToReplace === -1 ? 0 : indexToReplace] = updatedTime;
    }
    submitFunc(times?.length === 0 || applyForAllTimes, updatedCastTimes);
  };

  const getFormattedDate = date => {
    const momentDate = createDate(date, dateFormats.basic);
    const month = momentDate.format(dateFormats.productionMonth);
    const day = momentDate.format(dateFormats.date);
    return `${day} ${month}`;
  };

  const getFilteredRoles = (options, roleId, field) => {
    const roleOptions = get(options, roleId, []);
    const chosenRoles = get(castRef, `current.values.${field}.${roleId}`, []).map(role => role?.value);
    // to delete options that were chose
    return roleOptions.filter(option => !chosenRoles.includes(option?.value));
  };

  return (
    <SubmitModalLayout
      isOpened={isOpened}
      onClose={onClose}
      modalTitle={t(`${TP}.FN_EDIT_PERFORMANCE_CAST`)}
      title1={getFormattedDate(castDate)}
      title2={
        <div className={classes.title2}>
          <div className={classes.subTitleItem}>{production?.company?.name}</div>
          <Divider orientation="vertical" flexItem classes={{ root: classes.divider }} />
          <div className={classes.subTitleItem}>{production?.city?.name}</div>
          <Divider orientation="vertical" flexItem classes={{ root: classes.divider }} />
          <div className={classes.subTitleItem}>{production?.country?.name}</div>
        </div>
      }
      footer={
        <div>
          <Button onClick={() => submitCastAndTime(false)} className={classes.footerBtn}>
            {t(`${TP}.FN_APPLY_FOR_THIS`)}
          </Button>
          {times && times.length > 1 && (
            <Button onClick={() => submitCastAndTime(true)} variant="outlined">
              {t(`${TP}.FN_APPLY_FOR_ALL_TIMES`)}
            </Button>
          )}
        </div>
      }
    >
      {times && times.length > 1 && (
        <EditModeCardSection>
          <Title size="big" className={classes.boldText}>
            {t(`${TP}.FN_CHOOSE_TIME`)}
          </Title>
          <RadioGroup
            row
            name="time"
            value={timeValue?.time}
            onChange={event => {
              setTimeValue(find(times, { time: event.target.value }));
            }}
          >
            {times.map(timeInfo => (
              <FormControlLabel
                key={timeInfo?.time}
                value={timeInfo?.time}
                control={<Radio />}
                label={timeInfo?.time}
              />
            ))}
          </RadioGroup>
        </EditModeCardSection>
      )}
      <Divider />
      <EditModeCardSection>
        <div>
          <Title size="medium">{t(`${TP}.FN_ADD_EXIST_PRODUCTION_CAST_MEMBERS`)}</Title>
        </div>
        <Formik initialValues={currentCast} enableReinitialize onSubmit={getCast} innerRef={castRef}>
          {({ values }) => (
            <Grid container spacing={2}>
              {professions?.length > 0 && (
                <>
                  <Grid item sm={12}>
                    <Title size="big" style={{ fontWeight: 'bold' }} className={classes.sectionTitle}>
                      {t(`${TP}.FN_CREW`)}
                    </Title>
                  </Grid>
                  <Hidden xsDown>
                    <Grid item sm={4}>
                      <Title size="medium">{t(`${TP}.m_JOBTYPE`)}</Title>
                    </Grid>
                    <Grid item sm={8}>
                      <Title size="medium">{t(`${TP}.m_ARTISTNAME`)}</Title>
                    </Grid>
                  </Hidden>
                </>
              )}
              <CastFieldArray
                values={values}
                roles={professions}
                castType="crew"
                setFieldNameToFocus={setFieldNameToFocus}
                fieldToFocus={fieldToFocus}
                getFilteredRoles={getFilteredRoles}
                options={professionsOptions}
                classes={classes}
              />
              {roles?.length > 0 && (
                <>
                  <Grid item sm={12}>
                    <Title size="big" style={{ fontWeight: 'bold' }} className={classes.sectionTitle}>
                      {t(`${TP}.m_PRODUCTION_CAST`)}
                    </Title>
                  </Grid>
                  <Hidden xsDown>
                    <Grid item sm={4}>
                      <Title size="medium">{t(`${TP}.m_ROLE`)}</Title>
                    </Grid>
                    <Grid item sm={8}>
                      <Title size="medium">{t(`${TP}.m_ARTISTNAME`)}</Title>
                    </Grid>
                  </Hidden>
                </>
              )}
              <CastFieldArray
                values={values}
                roles={roles}
                castType="cast"
                setFieldNameToFocus={setFieldNameToFocus}
                fieldToFocus={fieldToFocus}
                getFilteredRoles={getFilteredRoles}
                options={rolesOptions}
                classes={classes}
              />
            </Grid>
          )}
        </Formik>
      </EditModeCardSection>
    </SubmitModalLayout>
  );
};

CastEditModal.propTypes = {
  performance: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  submitFunc: PropTypes.func.isRequired,
  isOpened: PropTypes.bool,
  professions: PropTypes.array.isRequired,
  roles: PropTypes.array.isRequired,
};

CastEditModal.defaultProps = {
  isOpened: false,
};

export default CastEditModal;
